import { GameState } from '../../core/GameState.js';
import { estimateRouteSchedule } from '../../utils/routeEta.js';
import { hoursText, flagsTitle } from '../panels/routeEtaInfo.js';

/**
 * Route editor timing (#53-T9e): per-stop badge and loop summary in plan mode
 * (stop 0, empty hold, second loop reported). Computed once per _render().
 */

/**
 * Pure text builder.
 * @param {object|null} schedule  estimateRouteSchedule result (plan mode) or null when no ship
 * @returns {{summary:{text:string, hint:boolean}|null, badges:Array<{text:string,title:string}|null>}}
 *   badges[i] belongs to stop i; null = no badge.
 */
export function describeEditorEta(schedule, { hasShip, stopCount, opportunistic }) {
  if (stopCount < 2 || opportunistic) return { summary: null, badges: [] };
  if (!hasShip) return { summary: { text: 'Assign a ship to see times', hint: true }, badges: [] };
  if (!schedule || schedule.blockedReason === 'no-manager') {
    return { summary: { text: 'Select a manager to see times', hint: true }, badges: [] };
  }
  if (schedule.blockedReason || !schedule.loop) return { summary: null, badges: [] };

  const badges = [];
  for (const s of schedule.stops) {
    const parts = [];
    if (s.travelHours > 0) parts.push(`${hoursText(s.travelHours)} sail`);
    if (s.portHoursSource === 'estimate') parts.push(`~${hoursText(s.portHours)} port`);
    badges[s.stopIndex] = parts.length ? { text: `→ ${parts.join(' - ')}`, title: flagsTitle(s) } : null;
  }
  return {
    summary: { text: `Loop ~${hoursText(schedule.loop.hours)} (estimate)`, hint: false },
    badges
  };
}

/** Run the estimate and build the markup pieces for one render. */
export function routeEditorEta(route) {
  const ship = route.shipId ? GameState.getShip(route.shipId) : null;
  const manager = GameState.routeManagers?.get(route.managerId) || null;
  const opportunistic = route.mode === 'opportunistic';
  const schedule = ship && !opportunistic && route.stops.length >= 2
    ? estimateRouteSchedule(route, ship, {
      mode: 'plan',
      now: GameState.currentDate,
      getPort: id => GameState.getPort(id),
      manager,
      warehouses: GameState.warehouses,
      portStock: id => GameState.portStock?.get(id)
    })
    : null;
  const d = describeEditorEta(schedule, { hasShip: !!ship, stopCount: route.stops.length, opportunistic });
  return {
    summaryHtml: d.summary
      ? `<div class="route-eta-summary${d.summary.hint ? ' route-eta-summary--hint' : ''}">${d.summary.text}</div>` : '',
    badgeHtml: idx => {
      const b = d.badges[idx];
      return b ? `<span class="stop-eta-badge"${b.title ? ` title="${b.title}"` : ''}>${b.text}</span>` : '';
    }
  };
}
